import React, { useState, useEffect } from 'react';
import { Modal, Button, Input } from '@/components/ui';
import type { MaintenanceLog, LogStatus } from '../types/maintenance-logs.types';
import { useLogs } from '../hooks/useLogs';
import { toast } from 'sonner';

interface LogFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  item?: MaintenanceLog | null;
  companyId: string;
}

const statusOptions: { value: LogStatus; label: string }[] = [
  { value: 'draft', label: 'Bản nháp' },
  { value: 'submitted', label: 'Gửi duyệt' },
];

export function LogFormModal({ isOpen, onClose, item, companyId }: LogFormModalProps) {
  const isEdit = !!item;
  const { createLog, isCreating } = useLogs(companyId);

  const [formData, setFormData] = useState<Partial<MaintenanceLog>>({
    task_name: '',
    task_type: 'routine',
    log_date: '',
    start_time: '',
    end_time: '',
    quantity_actual: 0,
    unit: 'm2',
    status_code: 'draft',
    project_id: '',
    zone_id: '',
    work_order_id: '',
    description: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (item) {
        setFormData(item);
      } else {
        setFormData({
          task_name: '',
          task_type: 'routine',
          log_date: new Date().toISOString().split('T')[0],
          start_time: '08:00',
          end_time: '17:00',
          quantity_actual: 0,
          unit: 'm2',
          status_code: 'draft',
          project_id: '',
          zone_id: '',
          description: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, item, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!isEdit) {
        await createLog({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật nhật ký thành công' : 'Ghi nhật ký mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Chỉnh sửa Nhật ký' : 'Ghi Nhật ký bảo dưỡng mới'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Tên công việc *"
            required
            value={formData.task_name || ''}
            onChange={(e) => setFormData({ ...formData, task_name: e.target.value })}
            placeholder="VD: Cắt cỏ Zone A"
          />
          <Input
            label="Loại công việc *"
            required
            value={formData.task_type || ''}
            onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
            placeholder="VD: routine, emergency..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Ngày thực hiện *"
            type="date"
            required
            value={formData.log_date || ''}
            onChange={(e) => setFormData({ ...formData, log_date: e.target.value })}
          />
          <Input
            label="Bắt đầu"
            type="time"
            value={formData.start_time || ''}
            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
          />
          <Input
            label="Kết thúc"
            type="time"
            value={formData.end_time || ''}
            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="ID Dự án *"
            required
            value={formData.project_id || ''}
            onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
          />
          <Input
            label="ID Khu vực (Zone)"
            value={formData.zone_id || ''}
            onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Khối lượng thực tế *"
              type="number"
              required
              value={formData.quantity_actual || 0}
              onChange={(e) => setFormData({ ...formData, quantity_actual: parseFloat(e.target.value) })}
            />
          </div>
          <Input
            label="Đơn vị *"
            required
            value={formData.unit || ''}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            placeholder="m2, cây..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || ''}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as LogStatus })}
            >
              {statusOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <Input
            label="ID Lệnh công việc (Nếu có)"
            value={formData.work_order_id || ''}
            onChange={(e) => setFormData({ ...formData, work_order_id: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mô tả công việc</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[80px]"
            placeholder="Chi tiết các hạng mục đã hoàn thành hoặc lưu ý phát sinh..."
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isCreating}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isCreating}>
            {isEdit ? 'Lưu thay đổi' : 'Ghi nhật ký'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
